import { useApolloClient, useMutation } from "@apollo/client";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { type FormEvent, useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import {
  DismissFinanceNotificationDocument,
  type DismissFinanceNotificationMutation,
  type DismissFinanceNotificationMutationVariables,
} from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";
import { financeNotificationErrorCode } from "../../lib/financeNotifications";
import { dialogActionsSx, dialogContentSx, dialogTitleSx } from "../../theme/dialogStyles";

const TITLE_ID = "dismiss-finance-notification-title";

// The queries that show open notifications: the inbox and the header count.
const OPEN_NOTIFICATION_QUERIES = ["FinanceNotifications", "FinanceNotificationCount"];

type DismissFinanceNotificationDialogProps = {
  notificationId: string;
  onClose: () => void;
};

// Takes a notification off the inbox with the reason Finance gives.
export const DismissFinanceNotificationDialog = ({
  notificationId,
  onClose,
}: DismissFinanceNotificationDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const client = useApolloClient();
  const [reason, setReason] = useState("");
  const [dismiss, { loading }] = useMutation<
    DismissFinanceNotificationMutation,
    DismissFinanceNotificationMutationVariables
  >(DismissFinanceNotificationDocument);

  const trimmedReason = reason.trim();
  const canSubmit = trimmedReason !== "" && !loading;

  const handleClose = () => {
    if (!loading) onClose();
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    try {
      await dismiss({
        variables: { id: notificationId, reason: trimmedReason },
        refetchQueries: OPEN_NOTIFICATION_QUERIES,
      });
    } catch (failure) {
      const code = financeNotificationErrorCode(failure);
      if (code === null) captureException(failure, "finance/notifications: dismiss");
      notify(translate(code ? `financeNotifications.errors.${code}` : "financeNotifications.dismiss.error"), {
        type: "error",
      });
      // Someone else handled it, or it is gone: the inbox no longer holds it.
      if (code === "notification-not-open" || code === "notification-not-found") {
        onClose();
        await client.refetchQueries({ include: OPEN_NOTIFICATION_QUERIES });
      }
      return;
    }
    notify(translate("financeNotifications.dismiss.success"), { type: "success" });
    onClose();
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby={TITLE_ID}
      PaperProps={{ component: "form", onSubmit: handleSubmit }}
    >
      <DialogTitle id={TITLE_ID} sx={dialogTitleSx}>
        {translate("financeNotifications.dismiss.title")}
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <TextField
          label={translate("financeNotifications.dismiss.reason")}
          value={reason}
          onChange={event => setReason(event.target.value)}
          required
          multiline
          minRows={3}
          autoFocus
          fullWidth
          disabled={loading}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button type="button" onClick={handleClose} disabled={loading}>
          {translate("ra.action.cancel")}
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!canSubmit}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {translate("financeNotifications.dismiss.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
